import React from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { I18n } from 'aws-amplify/utils';

/**
 * Sign-in screen (boards 8a, 8b). Wraps the Amplify Authenticator and only
 * changes layout, copy and styling; the Amplify auth flow itself is untouched.
 * Light theme only: no theme toggle and no dark styles.
 */

interface CustomAuthenticatorProps {
  children: (props: { signOut?: () => void; user?: any }) => React.ReactElement;
}

/* ---------------------------------------------------------------------------
 * Copy
 * ------------------------------------------------------------------------- */

/**
 * State 1: wrong credentials. Says the pair does not match and nothing else,
 * so it never reveals whether the account exists.
 */
export const WRONG_CREDENTIALS_MESSAGE =
  "That email and password don't match. Check both and try again, or choose Forgot password? to reset it.";

/** State 2: shown before the user types a new password after a temporary one. */
export const PASSWORD_POLICY: readonly string[] = [
  'At least 8 characters',
  'Upper case and lower case letters',
  'A number',
  'A symbol, for example ! ? # or $',
];

export const ADMIN_ACCOUNTS_NOTE =
  "Accounts are created by your organisation's administrator. If you need access, ask them to set you up.";

/**
 * Amplify shows Cognito's message for a failed sign-in. Both Cognito messages
 * that mean "wrong credentials" (wrong password, or no such user) get the same
 * plain-language text, so the screen does not reveal whether an account exists.
 */
I18n.putVocabulariesForLanguage('en', {
  'Incorrect username or password.': WRONG_CREDENTIALS_MESSAGE,
  'Incorrect username or password': WRONG_CREDENTIALS_MESSAGE,
  'User does not exist.': WRONG_CREDENTIALS_MESSAGE,
  'User does not exist': WRONG_CREDENTIALS_MESSAGE,
});
I18n.setLanguage('en');

/* ---------------------------------------------------------------------------
 * Presentational pieces
 * ------------------------------------------------------------------------- */

const AuthLogo: React.FC = () => (
  <img
    src="/clairvoya_horizontal_color.png"
    alt="Clairvoya"
    className="block h-[46px] w-auto"
  />
);

const AuthHeading: React.FC<{ title: string; children?: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-5 mt-6">
    <h1 className="m-0 text-2xl font-bold text-gray-900">{title}</h1>
    {children && (
      <p className="m-0 mt-1.5 text-sm leading-snug text-gray-600">{children}</p>
    )}
  </div>
);

export const PasswordPolicy: React.FC = () => (
  <div
    role="group"
    aria-label="Password rules"
    className="mb-4 rounded-lg border border-[#EDE7FF] bg-[#F5F3FF] px-3.5 py-3"
  >
    <p className="m-0 mb-1.5 text-xs font-bold uppercase tracking-widest text-gray-500">
      Your new password needs
    </p>
    <ul className="m-0 list-disc pl-5 text-sm leading-relaxed text-gray-700">
      {PASSWORD_POLICY.map((rule) => (
        <li key={rule}>{rule}</li>
      ))}
    </ul>
  </div>
);

/** State 2 header: the policy is listed above the password fields. */
export const NewPasswordHeader: React.FC = () => (
  <div>
    <AuthHeading title="Set a new password">
      You signed in with a temporary password. Choose a new one that follows these
      rules before you continue.
    </AuthHeading>
    <PasswordPolicy />
  </div>
);

export interface ResetCodeSentNoticeProps {
  /** Address (as Cognito returns it, usually partly masked) the code was sent to. */
  destination?: string;
  medium?: string;
}

/** State 3: says what was sent and where. */
export const ResetCodeSentNotice: React.FC<ResetCodeSentNoticeProps> = ({
  destination,
  medium,
}) => {
  const what =
    medium === 'SMS' || medium === 'PHONE'
      ? 'a verification code by text message'
      : 'a verification code by email';
  return (
    <AuthHeading title="Check your email">
      {destination ? (
        <>
          We sent {what} to <strong className="text-gray-900">{destination}</strong>.
        </>
      ) : (
        <>We sent {what} to the address you entered.</>
      )}{' '}
      It can take a minute to arrive. Enter it below, then choose a new password.
    </AuthHeading>
  );
};

/* ---------------------------------------------------------------------------
 * Amplify slot components (module scope so their identity is stable)
 * ------------------------------------------------------------------------- */

const SignInHeader: React.FC = () => (
  <AuthHeading title="Sign in">
    Use the work email address your administrator set you up with.
  </AuthHeading>
);

const SignInFooter: React.FC = () => {
  const { toForgotPassword } = useAuthenticator((context) => [context.toForgotPassword]);
  return (
    <div>
      <div className="text-center">
        <button
          type="button"
          onClick={toForgotPassword}
          className="cursor-pointer border-0 bg-transparent p-0 text-sm font-bold text-[#5A21C9] hover:underline"
        >
          Forgot password?
        </button>
      </div>
      <p className="m-0 mt-[18px] border-t border-gray-100 pt-4 text-[13px] leading-normal text-gray-500">
        {ADMIN_ACCOUNTS_NOTE}
      </p>
    </div>
  );
};

const ForgotPasswordHeader: React.FC = () => (
  <AuthHeading title="Reset your password">
    Enter your email address and we will send you a verification code.
  </AuthHeading>
);

const ConfirmResetPasswordHeader: React.FC = () => {
  const { codeDeliveryDetails, username } = useAuthenticator((context) => [
    context.codeDeliveryDetails,
    context.username,
  ]);
  // Cognito returns either the v5 shape (Destination) or the v6 shape (destination).
  const details = (codeDeliveryDetails ?? {}) as Record<string, string | undefined>;
  const destination = details.Destination ?? details.destination ?? (username || undefined);
  const medium = details.DeliveryMedium ?? details.deliveryMedium;
  return <ResetCodeSentNotice destination={destination} medium={medium} />;
};

const components = {
  Header: AuthLogo,
  SignIn: { Header: SignInHeader, Footer: SignInFooter },
  ForceNewPassword: { Header: NewPasswordHeader },
  ForgotPassword: { Header: ForgotPasswordHeader },
  ConfirmResetPassword: { Header: ConfirmResetPasswordHeader },
};

const CustomAuthenticator = ({ children }: CustomAuthenticatorProps) => (
  <Authenticator
    className="clairvoya-auth"
    hideSignUp={true}
    loginMechanisms={['email']}
    components={components}
  >
    {children}
  </Authenticator>
);

export default CustomAuthenticator;
